/**
 * Selects features based on input geometry and optionally highlight them.
 * @module render-selector
 * @see {@link http://wiki.infor.com:8080/confluence/label/~CALIX/render-selector|blog}
 */

import ol = require("ol");
import _ = require("underscore");
import Portal = require("portal-gis");
import Utils = require("../utils");
import IPS = require("publicsector");
import Base = require("./base-command");
import Highlighter = require("./feature-highlight-handler");
import defaults = require("../models/defaults");

class Command extends Base<{
    in: any;
    out: {
        point?: ol.Coordinate;
        box?: ol.Coordinate;
        pixel?: ol.Pixel;
        layers?: Rhythm.GIS.FeatureQueryResultLayer[];
    }
}> {

    static about = `Selects features based on input geometry and optionally highlight them.`;

    static options = {
        "toggle-rule": 'Internal algorithm used to interpret toggle; "simple" does a literal toggle, "smart" select everything within the area unless everything is already selected, then it unselects everything.',
        "hilite": 'use built-in higlighter'
    };

    static defaults = {
        "toggle-rule": "simple",
        "hilite": "true"
    };

    // mark all features as selected
    execute(args = this.args) {
        _.defaults(this.options, Command.defaults);

        let selector = this.createSelector();
        let toggler = this.smartToggle;

        Utils.ifOption(this.options, "toggle-rule", toggleRule => {
            let t = this[`${toggleRule}Toggle`];
            if (typeof t === "function") {
                toggler = t;
            } else {
                console.warn("invalid toggle-rule, using simple");
                toggler = this.simpleToggle;
            }
        });

        let layers = this.findOption("layers");
        let features = toggler.call(this, layers, this.toggleFeature, selector);

        let eventArgs = {
            selected: features.filter(f => !!Utils.extend(f).selected),
            unselected: features.filter(f => !Utils.extend(f).selected)
        };

        // spoof a highlight handler (simplifies configuration, set highlight=false to disable)
        if (this.options["hilite"] !== "false") {
            let highlighter = new Highlighter(defaults.asCommandTemplate({ id: "feature-highlight-handler" }), this.mediator, {
                out: eventArgs
            });
            highlighter.execute();
        }

        // share results via trigger
        this.trigger(eventArgs);
    }

    private toggleFeature(f: Rhythm.GIS.FeatureQueryResultFeature) {
        let extensions = Utils.extend(f);
        extensions.selected = !extensions.selected;
    }

    private simpleSelector(f: Rhythm.GIS.FeatureQueryResultFeature) {
        return true;
    }

    private simpleToggle(layers: Rhythm.GIS.FeatureQueryResultLayer[], toggler = this.toggleFeature, selector = this.simpleSelector) {
        let features = [];
        layers.forEach(l => l.features.filter(selector).forEach(f => features.push(f)));
        features.forEach(toggler);
        return features;
    }

    private smartToggle(layers: Rhythm.GIS.FeatureQueryResultLayer[], toggler = this.toggleFeature, selector = this.simpleSelector) {
        let features = [];
        layers.forEach(l => l.features.filter(selector).forEach(f => features.push(f)));

        // mark all features without an extension as not selected
        //features.map(f => Utils.extend(f)).filter(ext => typeof ext.selected === "undefined").forEach(ext => ext.selected = false);

        // get features not explicitly selected
        let unselected = features.filter(f => !Utils.extend(f).selected);

        // if some (or all) are unselected, select those otherwise unselect the selected.
        if (0 < unselected.length) {
            unselected.forEach(toggler);
        } else {
            features.forEach(toggler);
        }

        return features;
    }

    /**
     * If a filter has been configured then it will run an attribute comparision filter implemented here
     * otherwise it runs a `simpleSelector` which ensures the feature is visible.
     *
        <EVENT event="address-selection" mid="app/commands/feature-selector" disabled="False" id="ags-address-selector">
            <OPTIONS>
            <Values about="Select addresses from the AGS server" value="{&quot;AddrType&quot;: &quot;A&quot;}" id="filter" />
            </OPTIONS>
        </EVENT>
     *
     */
    private createSelector() {
        let filter = this.options["filter"] && JSON.parse(this.options["filter"]);
        if (!filter) return this.simpleSelector;

        let keys = _.keys(filter);

        let selector = (f: Rhythm.GIS.FeatureQueryResultFeature) => {
            return this.simpleSelector(f) && keys.every(k => f.attributes[k] === filter[k]);
        };

        return selector;
    }
}

export = Command;